import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

export default function TwoFactorCard() {
  const [status, setStatus] = useState(null); // null=loading, {enabled}
  const [stage, setStage] = useState("idle"); // idle | enroll | confirm-disable
  const [enrollment, setEnrollment] = useState(null); // {secret, qr_data_uri, provisioning_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/2fa/status").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const beginEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setEnrollment(r.data);
      setStage("enroll");
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start 2FA setup");
    } finally { setBusy(false); }
  };

  const confirmEnroll = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify-setup", { code });
      toast.success("Two-Factor Authentication enabled");
      setStage("idle"); setCode(""); setEnrollment(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const beginDisable = () => { setStage("confirm-disable"); setCode(""); };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("Two-Factor Authentication disabled");
      setStage("idle"); setCode("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  if (status === null) {
    return (
      <div className="border border-border p-8 flex items-center gap-3 text-muted-foreground" data-testid="twofa-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading 2FA status…
      </div>
    );
  }

  return (
    <div className="border border-border p-8 space-y-4" data-testid="twofa-card">
      <div className="flex items-center gap-3">
        {status.enabled ? (
          <ShieldCheck className="w-5 h-5 text-primary" />
        ) : (
          <ShieldAlert className="w-5 h-5 text-amber-600" />
        )}
        <div className="font-overline text-accent">Two-Factor Authentication</div>
      </div>
      <p className="text-xs text-muted-foreground">
        Scan a QR code with Google Authenticator, Authy, or 1Password. We&apos;ll ask for a 6-digit code on every sign-in.
      </p>

      <div data-testid="twofa-state">
        <span className="font-overline text-muted-foreground">Status:</span>{" "}
        <span className={`font-mono-data uppercase ${status.enabled ? "text-primary" : "text-muted-foreground"}`}
          data-testid="twofa-status-label">
          {status.enabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {stage === "idle" && !status.enabled && (
        <Button onClick={beginEnroll} disabled={busy}
          className="rounded-none uppercase tracking-wider h-11" data-testid="twofa-enable-btn">
          {busy ? "…" : "Enable 2FA"}
        </Button>
      )}

      {stage === "idle" && status.enabled && (
        <Button onClick={beginDisable} variant="outline"
          className="rounded-none uppercase tracking-wider h-11" data-testid="twofa-disable-btn">
          Disable 2FA
        </Button>
      )}

      {stage === "enroll" && enrollment && (
        <div className="space-y-4 border-t border-border pt-4">
          <div>
            <div className="font-overline mb-2">Step 1 · Scan with your authenticator app</div>
            <img src={enrollment.qr_data_uri} alt="2FA QR code"
              className="border border-border bg-white w-48 h-48"
              data-testid="twofa-qr" />
          </div>
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">Can&apos;t scan? Enter this key manually</summary>
            <code className="block font-mono-data text-xs mt-2 break-all p-2 bg-muted" data-testid="twofa-secret">
              {enrollment.secret}
            </code>
          </details>
          <div>
            <Label className="font-overline">Step 2 · Enter the 6-digit code from your app</Label>
            <Input className="rounded-none mt-2 font-mono-data tracking-widest text-lg" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000" data-testid="twofa-verify-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={confirmEnroll} disabled={busy || code.length < 6}
              className="rounded-none uppercase tracking-wider h-11"
              data-testid="twofa-verify-submit">
              {busy ? "…" : "Verify & Enable"}
            </Button>
            <Button onClick={() => { setStage("idle"); setEnrollment(null); setCode(""); }}
              variant="outline" className="rounded-none uppercase tracking-wider h-11">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {stage === "confirm-disable" && (
        <div className="space-y-4 border-t border-border pt-4">
          <p className="text-sm">
            Enter the current 6-digit code from your authenticator app to confirm disabling 2FA.
          </p>
          <Input className="rounded-none font-mono-data tracking-widest text-lg" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="000000" data-testid="twofa-disable-input" />
          <div className="flex gap-2">
            <Button onClick={confirmDisable} disabled={busy || code.length < 6}
              variant="destructive" className="rounded-none uppercase tracking-wider h-11"
              data-testid="twofa-disable-submit">
              {busy ? "…" : "Confirm Disable"}
            </Button>
            <Button onClick={() => { setStage("idle"); setCode(""); }}
              variant="outline" className="rounded-none uppercase tracking-wider h-11">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
